/* WildQuest i18n — lightweight, dependency-free.
 *
 * - Locale JSON files are statically imported (bundled) so lookups are
 *   synchronous and instant.
 * - Auto-detects from navigator.language on first visit, then persists
 *   the user's choice in localStorage.wq_lang.
 * - Sets <html lang> and <html dir> on every language change.
 * - `t('a.b.c', 'fallback')` resolves dot-notation keys; falls back to
 *   English then to the raw key. Supports simple {var} interpolation.
 *
 * MVP scope: marketing surfaces only (Landing / Login / Membership / Upgrade).
 * Rest of the app remains English; keys can be added incrementally.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import pt from './locales/pt.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import zh from './locales/zh.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import ar from './locales/ar.json';
import hi from './locales/hi.json';

const BUNDLES = { en, es, fr, de, pt, ja, ko, zh, it, nl, ar, hi };

// Ordered so the switcher menu renders in a predictable, curated order.
export const LANGUAGES = [
  { code: 'en', native: 'English',    flag: '🇺🇸', dir: 'ltr' },
  { code: 'es', native: 'Español',    flag: '🇪🇸', dir: 'ltr' },
  { code: 'fr', native: 'Français',   flag: '🇫🇷', dir: 'ltr' },
  { code: 'de', native: 'Deutsch',    flag: '🇩🇪', dir: 'ltr' },
  { code: 'pt', native: 'Português',  flag: '🇵🇹', dir: 'ltr' },
  { code: 'it', native: 'Italiano',   flag: '🇮🇹', dir: 'ltr' },
  { code: 'nl', native: 'Nederlands', flag: '🇳🇱', dir: 'ltr' },
  { code: 'ja', native: '日本語',      flag: '🇯🇵', dir: 'ltr' },
  { code: 'ko', native: '한국어',      flag: '🇰🇷', dir: 'ltr' },
  { code: 'zh', native: '简体中文',    flag: '🇨🇳', dir: 'ltr' },
  { code: 'hi', native: 'हिन्दी',       flag: '🇮🇳', dir: 'ltr' },
  { code: 'ar', native: 'العربية',    flag: '🇸🇦', dir: 'rtl' },
];

const STORAGE_KEY = 'wq_lang';

function detectInitialLang() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && BUNDLES[stored]) return stored;
    const nav = (navigator.languages && navigator.languages[0]) || navigator.language || 'en';
    const short = nav.slice(0, 2).toLowerCase();
    if (BUNDLES[short]) return short;
  } catch {}
  return 'en';
}

function getByPath(obj, path) {
  if (!obj) return undefined;
  const parts = path.split('.');
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function interpolate(str, vars) {
  if (!vars || typeof str !== 'string') return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
}

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (k) => k,
  raw: () => undefined,
  dir: 'ltr',
});

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectInitialLang);
  const meta = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];
  const dir = meta.dir;

  useEffect(() => {
    try {
      document.documentElement.setAttribute('lang', lang);
      document.documentElement.setAttribute('dir', dir);
    } catch {}
  }, [lang, dir]);

  const setLang = useCallback((code) => {
    if (!BUNDLES[code]) return;
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
    setLangState(code);
  }, []);

  const t = useCallback(
    (key, fallbackOrVars, maybeVars) => {
      const [fallback, vars] =
        typeof fallbackOrVars === 'string' || fallbackOrVars === undefined
          ? [fallbackOrVars, maybeVars]
          : [undefined, fallbackOrVars];
      const val = getByPath(BUNDLES[lang], key);
      if (typeof val === 'string') return interpolate(val, vars);
      const enVal = getByPath(BUNDLES.en, key);
      if (typeof enVal === 'string') return interpolate(enVal, vars);
      return fallback !== undefined ? interpolate(fallback, vars) : key;
    },
    [lang]
  );

  // Raw lookup — returns whatever type is at `key` (string, array, object)
  // in the current language, falling back to English. Undefined if missing.
  const raw = useCallback(
    (key) => {
      const v = getByPath(BUNDLES[lang], key);
      if (v !== undefined) return v;
      return getByPath(BUNDLES.en, key);
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, setLang, t, raw, dir }), [lang, setLang, t, raw, dir]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

// Convenience hook when only the `t` function is needed.
export function useT() {
  return useContext(I18nContext).t;
}
